
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import UIInlineSwitcher from '~/components/Common/UI2/InlineSwitcher/UIInlineSwitcher.vue'
import { routerVcsMap } from '~/plugins/helpers/provider'

const GITHUB_PROVIDERS: routerVcsMap[] = [routerVcsMap.gh]

type CoverageMethod = 'oidc' | 'dsn'
const METHOD_OIDC: CoverageMethod = 'oidc'
const METHOD_DSN: CoverageMethod = 'dsn'
const METHOD_LABELS: Record<CoverageMethod, string> = {
  oidc: 'OIDC (Recommended)',
  dsn: 'DSN'
}

export default defineComponent({
  name: 'EnableCoverageSteps',
  components: {
    UIInlineSwitcher
  },
  props: {
    dsn: {
      type: String,
      required: true
    },
    vcsProvider: {
      type: String as PropType<routerVcsMap>,
      required: true,
      validator: (v: string) => Object.values(routerVcsMap).includes(v as routerVcsMap)
    }
  },

  data() {
    return {
      chevronIconAnimateClass: '',
      // Empty until the user explicitly picks a method; the effective method is
      // derived reactively in `selectedMethod` so it stays correct across SPA navigation.
      userSelectedMethod: '' as CoverageMethod | ''
    }
  },
  computed: {
    isGitHub(): boolean {
      return GITHUB_PROVIDERS.includes(this.vcsProvider)
    },
    selectedMethod(): CoverageMethod {
      return this.userSelectedMethod || (this.isGitHub ? METHOD_OIDC : METHOD_DSN)
    },
    isOidcSelected(): boolean {
      return this.isGitHub && this.selectedMethod === METHOD_OIDC
    },
    methodOptions(): string[] {
      return [METHOD_LABELS.oidc, METHOD_LABELS.dsn]
    },
    selectedMethodLabel(): string {
      return METHOD_LABELS[this.selectedMethod]
    },
    oidcPermissionsYaml(): string {
      return 'permissions:\n  id-token: write\n  contents: read'
    },
    oidcCliCommand(): string {
      const host = this.$config.onPrem
        ? `https://${this.$config.domain}`
        : 'https://app.deepsource.com'
      return `./bin/deepsource report --analyzer test-coverage --key <key> --value-file <path-to-coverage-report> --use-oidc --host ${host}`
    },
    cliCommand(): string {
      const base =
        './bin/deepsource report --analyzer test-coverage --key <key> --value-file <path-to-coverage-report>'
      return this.$config.onPrem ? `${base} --host https://${this.$config.domain}` : base
    },
    reportCommand(): string {
      return this.isOidcSelected ? this.oidcCliCommand : this.cliCommand
    },
    installationCommand(): string {
      return 'curl -fsSL https://cli.deepsource.com/install | BINDIR=./bin sh'
    }
  },
  methods: {
    selectMethod(label: string): void {
      this.userSelectedMethod = label === METHOD_LABELS.oidc ? METHOD_OIDC : METHOD_DSN
    },
    isOpenHandler(open: boolean): void {
      this.chevronIconAnimateClass = open
        ? 'animate-first-quarter-spin rotate-90'
        : 'animate-reverse-quarter-spin rotate-0'
    }
  }
})
